import React, { useEffect, useRef } from 'react';

interface CassetteLoaderProps {
  size?: number;
  className?: string;
  variant?: 'inline' | 'button' | 'overlay';
  text?: string;
}

const CassetteLoader: React.FC<CassetteLoaderProps> = ({ 
  size = 80, 
  className = '', 
  variant = 'overlay',
  text 
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    canvas.width = size;
    canvas.height = size;
    
    // Scale context to match size
    const scale = size / 80;
    ctx.scale(scale, scale);

    let frame = 0;
    const animate = () => {
      // Clear canvas
      ctx.clearRect(0, 0, 80, 80);
      
      // Draw cassette body (white with black outline)
      ctx.fillStyle = '#ffffff';
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.fillRect(10, 20, 60, 40);
      ctx.strokeRect(10, 20, 60, 40);
      
      // Draw corner dots (screw holes)
      ctx.fillStyle = '#000000';
      ctx.beginPath();
      ctx.arc(15, 25, 1.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(65, 25, 1.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(15, 55, 1.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(65, 55, 1.5, 0, Math.PI * 2);
      ctx.fill();
      
      // Draw left reel (spinning)
      const leftReelAngle = (frame * 0.1) % (Math.PI * 2);
      ctx.save();
      ctx.translate(25, 40);
      ctx.rotate(leftReelAngle);
      
      // Reel center (white with black outline)
      ctx.fillStyle = '#ffffff';
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(0, 0, 8, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      
      // Reel orbiting dots (instead of spokes)
      ctx.fillStyle = '#000000';
      for (let i = 0; i < 6; i++) {
        const angle = (i * Math.PI) / 3;
        const x = Math.cos(angle) * 6;
        const y = Math.sin(angle) * 6;
        ctx.beginPath();
        ctx.arc(x, y, 1.5, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
      
      // Draw right reel (spinning in same direction as left)
      const rightReelAngle = (frame * 0.1) % (Math.PI * 2);
      ctx.save();
      ctx.translate(55, 40);
      ctx.rotate(rightReelAngle);
      
      // Reel center (white with black outline)
      ctx.fillStyle = '#ffffff';
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(0, 0, 8, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      
      // Reel orbiting dots (instead of spokes)
      ctx.fillStyle = '#000000';
      for (let i = 0; i < 6; i++) {
        const angle = (i * Math.PI) / 3;
        const x = Math.cos(angle) * 6;
        const y = Math.sin(angle) * 6;
        ctx.beginPath();
        ctx.arc(x, y, 1.5, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
      
      // Draw tape window (smaller rectangle with black outline)
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 1.5;
      ctx.strokeRect(35, 32, 10, 16);
      
      // Draw tape line with vibrating effect
      const tapeVibration = Math.sin(frame * 0.3) * 0.5;
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(35, 40 + tapeVibration);
      ctx.lineTo(45, 40 + tapeVibration);
      ctx.stroke();
      
      // Draw tape rollers/guides (small dots along tape)
      ctx.fillStyle = '#000000';
      ctx.beginPath();
      ctx.arc(37, 40 + tapeVibration, 1, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(40, 40 + tapeVibration, 1, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(43, 40 + tapeVibration, 1, 0, Math.PI * 2);
      ctx.fill();
      
      frame++;
      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [size]);

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <canvas
        ref={canvasRef}
        className="block"
        style={{ width: size, height: size }}
      />
      {text && variant === 'overlay' && (
        <div className="text-sm font-medium text-gray-500">{text}</div>
      )}
    </div>
  );
};

export default CassetteLoader;

